import React, { useState, useEffect } from 'react';
import { competitors as competitorsApi } from '../../api/client';
import { Users, Plus, Trash2, Loader2, Globe } from 'lucide-react';

export default function CompetitorsPage({ activeBrandId }) {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);

  const loadData = async () => {
    if (!activeBrandId) return;
    setLoading(true);
    try {
      const data = await competitorsApi.list(activeBrandId);
      setComps(data.competitors || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [activeBrandId]);

  const handleAdd = async () => {
    if (!newName.trim() || !newDomain.trim()) return;
    setAdding(true);
    try {
      await competitorsApi.add(activeBrandId, { name: newName, domain: newDomain });
      setNewName('');
      setNewDomain('');
      setShowAdd(false);
      loadData();
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const handleDelete = async (compId) => {
    if (!window.confirm('Remove this competitor?')) return;
    try {
      await competitorsApi.delete(activeBrandId, compId);
      loadData();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Intelligence</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Competitors</h1>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-4 py-1.5 text-sm bg-stone-900 text-white dark:bg-white dark:text-stone-900 flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add Competitor
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAdd(false)} />
          <div className="relative bg-white dark:bg-stone-900 w-full max-w-lg border border-stone-200 dark:border-stone-700 shadow-xl p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Add Competitor</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" placeholder="Competitor name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Domain</label>
                <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" placeholder="competitor.com" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm border border-stone-200 text-stone-600">Cancel</button>
              <button onClick={handleAdd} disabled={adding} className="px-4 py-2 text-sm bg-stone-900 text-white flex items-center gap-1">
                {adding && <Loader2 className="w-4 h-4 animate-spin" />} Add
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : (
        <div className="space-y-3">
          {comps.length === 0 ? (
            <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-8 text-center text-stone-500">No competitors added yet.</div>
          ) : (
            comps.map((comp) => (
              <div key={comp.competitor_id} className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 hover:border-stone-400 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded flex items-center justify-center bg-stone-100 dark:bg-stone-700">
                      <Globe className="w-5 h-5 text-stone-500" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900 dark:text-white">{comp.name}</p>
                      <p className="text-sm text-stone-500">{comp.domain}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="font-serif text-xl text-stone-900 dark:text-white">{comp.visibility_score}</div>
                      <div className="text-xs text-stone-400">Visibility</div>
                    </div>
                    <button onClick={() => handleDelete(comp.competitor_id)} className="p-2 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded" title="Remove">
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
